import { Component, Inject, Vue, Watch } from "vue-property-decorator";
import {
  SPACE_CONSTANTS,
  SPACE_VALIDATORS,
  SPACES_ACCESS
} from "../../constants/SpaceConstants";
import ModalTemplate from "../../../../shared/components/ModalTemplate/ModalTemplate.vue";
import { SpaceService } from "../../services/SpaceService";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import {
  COLLECTIONS_NAME,
  DIALOG,
  FORM_CONSTANTS,
  FORM_MESSAGES,
  FORM_VALIDATORS
} from "@/shared/constants/FormConstants";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { Http } from "@status/codes";
import { Space } from "../../models/Space";
import { Response } from "@/shared/models/Response";
import { CampusService } from "@/modules/Campus/services/CampusService";
import { CampusModel } from "@/modules/Campus/models/CampusModel";
import { CampusQuery } from "@/modules/Campus/models/CampusQuery";
import { STATE, GENERAL_CONSTANTS } from "@/shared/constants/GeneralConstants";
import { AxiosError } from "axios";
import { CAMPUS_ACCESS } from "@/modules/Campus/constants/CampusConstants";
import { Utils } from "@/shared/utils/utils";
import { DynamicSelectConfig } from "@/shared/components/DynamicSelect/model/DynamicSelectConfig";
import DynamicSelect from "@/shared/components/DynamicSelect/DynamicSelect.vue";

@Component({
  name: "SaveSpace",
  components: {
    ModalTemplate,
    DynamicSelect
  }
})
export default class SaveSpace extends Vue {
  @Inject() spaceService!: SpaceService;
  @Inject() campusService!: CampusService;

  cardClass = STYLE_CLASSES.CARD;
  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  switchClass = STYLE_CLASSES.SWITCH;
  iconBtnClass = STYLE_CLASSES.ICON_BTN;
  INACTIVE_CAMPUS = STYLE_CLASSES.OUTTER_BORDER_ELEMENT_VOIDED;

  pageTitle = SPACE_CONSTANTS.PAGE_TITLE_CREATE;
  SPACE_NAME = SPACE_CONSTANTS.LABEL.NAME;
  SESSION_URL = SPACE_CONSTANTS.LABEL.STATE;
  SESSION_STATE = FORM_CONSTANTS.LABEL.STATE;
  SAVE_BTN = FORM_CONSTANTS.BUTTON.SAVE;
  CANCEL_BTN = FORM_CONSTANTS.BUTTON.CANCEL;
  ADD_CAMPUS = SPACE_CONSTANTS.LABEL.ADD_CAMPUS;

  requiredField = [
    FORM_VALIDATORS.REQUIRED_FIELD,
    FORM_VALIDATORS.NOT_BLANK_SPACES_VALIDATOR
  ];
  maxLength = [SPACE_VALIDATORS.MAX_LENGTH];
  urlPattern = [FORM_VALIDATORS.URL_PATTERN];

  spaceModForm = false;
  spaceName = "";
  state = true;
  spaceId = "";
  isEditing = false;
  modalDialog = false;
  modalText = "";
  modalTitle = "";
  space!: Space;
  campusName = "";
  campusChosen: CampusModel = new CampusModel();
  listNameCampus: CampusModel[] = [];
  schemaReady = true;
  campusIsInactive = false;
  errorsName: string[] = [];
  campusConfigList: DynamicSelectConfig = {
    multiple: false,
    default: [],
    listAll: [],
    messageLabel: this.ADD_CAMPUS,
    required: true
  };
  campusModel = [{ id: "", name: "" }];
  lastRef = "";
  uniqueName = false;

  async mounted() {
    await this.loadCampus();
    if (this.$route.params.id) {
      this.isEditing = true;
      this.spaceId = this.$route.params.id;
      await this.getSpaceById();
      await this.loadInactiveCampusInUse();
      this.pageTitle = SPACE_CONSTANTS.PAGE_TITLE_EDIT;
    }
  }

  get campus() {
    return this.campusModel[0].id;
  }

  set campus(id: string) {
    const campus = this.campusConfigList.listAll.find(item => item.id == id);
    this.campusModel = [{ id: campus?.id ?? "", name: campus?.name ?? "" }];
  }

  @Watch("campus")
  handleOnCampusChange() {
    this.validateUniqueName();
    this.campusIsInactive =
      this.listNameCampus.find(campus => campus._key === this.campus)?.state ===
      STATE.INACTIVE;
  }

  async getSpaceById() {
    try {
      const response = await this.spaceService.getSpaceById(this.spaceId);

      if (response.code === Http.Found && response.data) {
        this.space = response.data;
        this.fillSpaceForm();
      } else {
        Snackbar.popInfo(response.message);
      }
    } catch (error) {
      const errorMessage =
        (error as Error).message === GENERAL_CONSTANTS.FORBIDDEN
          ? `${FORM_MESSAGES.FORBIDDEN_REQUEST}${SPACES_ACCESS.GET}`
          : FORM_MESSAGES.REQUEST_FAILED;

      Snackbar.popError(errorMessage);
    }
  }

  fillSpaceForm() {
    this.spaceName = this.space.name;
    this.state =
      this.space.state === FORM_CONSTANTS.STATE.ACTIVE ? true : false;
    this.campus = this.space.campus;
  }

  confirmDialog(operation: string) {
    if (operation === DIALOG.SAVE) {
      this.modalTitle = FORM_MESSAGES.SAVE_MODAL.TITLE;
      this.modalText = FORM_MESSAGES.SAVE_MODAL.MESSAGE;
    } else {
      this.modalTitle = FORM_MESSAGES.CANCEL_MODAL.TITLE;
      this.modalText = FORM_MESSAGES.CANCEL_MODAL.MESSAGE;
    }

    this.modalDialog = true;
  }

  async handleModalAccept() {
    this.modalDialog = false;

    if (this.modalTitle === FORM_MESSAGES.SAVE_MODAL.TITLE) {
      await this.saveSpace();
    } else if (this.modalTitle === FORM_MESSAGES.CANCEL_MODAL.TITLE) {
      this.$router.go(-1);
    }
  }

  handleModalCancel() {
    this.modalDialog = false;
  }

  async saveSpace() {
    try {
      if (this.campus === "") {
        Snackbar.popWarning(FORM_CONSTANTS.SNACKBAR_MESSAGES.CAMPUS_EMPTY);
        return;
      }

      const spaceToSave: Space = {
        name: this.spaceName,
        state: this.state
          ? FORM_CONSTANTS.STATE.ACTIVE
          : FORM_CONSTANTS.STATE.INACTIVE,
        campus: this.campus
      };

      let response: Response<string | void>;

      if (this.isEditing) {
        response = await this.spaceService.updateSpace(
          this.spaceId,
          spaceToSave
        );
      } else {
        response = await this.spaceService.addSpace(spaceToSave);

        if (response.code == Http.Found) {
          Snackbar.popWarning(response.message);
          return;
        }
      }

      if (response.code === Http.Ok) {
        Snackbar.popSuccess(response.message);
        this.$router.push("/space");
      } else {
        Snackbar.popError(response.message);
      }
    } catch (error) {
      const METHOD = Utils.getMethodHTTP(
        error as AxiosError
      ) as keyof typeof SPACES_ACCESS;
      const errorMessage =
        (error as Error).message === GENERAL_CONSTANTS.FORBIDDEN
          ? `${FORM_MESSAGES.FORBIDDEN_REQUEST}${SPACES_ACCESS[METHOD]}`
          : FORM_MESSAGES.REQUEST_FAILED;

      Snackbar.popError(errorMessage);
    }
  }

  async handleCampus(name: string) {
    const responseData = await this.campusService.getCampusByFilter({
      name,
      state: [STATE.ACTIVE]
    });

    if (responseData.code === Http.Found && responseData.data) {
      this.campusConfigList.listAll = [
        ...this.campusConfigList.listAll,
        ...responseData.data.data.map(item => {
          return { id: item._key!, name: item.name };
        })
      ];
    }
  }

  async loadCampus(name?: string) {
    this.campusConfigList.notFound = false;
    if (name) {
      this.handleCampus(name);
      return;
    }
    const campusQuery: CampusQuery = {
      state: [STATE.ACTIVE],
      docref: this.lastRef
    };
    try {
      const responseData = await this.campusService.getCampusByFilter(
        campusQuery
      );

      if (responseData.code === Http.Found && responseData.data !== undefined) {
        this.listNameCampus = responseData.data.data;
        if (this.lastRef) {
          this.campusConfigList.listAll = [
            ...this.campusConfigList.listAll,
            ...responseData.data.data.map(item => {
              return { id: item._key!, name: item.name };
            })
          ];
        } else {
          this.campusConfigList.listAll = responseData.data.data.map(item => {
            return { id: item._key!, name: item.name };
          });
        }

        this.lastRef = responseData.data.docref;
      } else {
        if (!this.lastRef)
          Snackbar.popInfo(SPACE_CONSTANTS.MESSAGE.NOT_FOUND_CAMPUS);
        this.campusConfigList.notFound = true;
        this.listNameCampus = [];
      }
    } catch (error) {
      const errorMessage =
        (error as Error).message === GENERAL_CONSTANTS.FORBIDDEN
          ? `${FORM_MESSAGES.FORBIDDEN_REQUEST}${CAMPUS_ACCESS.GET}`
          : FORM_MESSAGES.REQUEST_FAILED;

      Snackbar.popError(errorMessage);
    }
  }

  async loadInactiveCampusInUse() {
    const inactiveCampusInUse = this.listNameCampus.filter(
      campus => campus._key === this.space.campus
    );

    try {
      if (inactiveCampusInUse.length === 0) {
        const response = await this.campusService.getCampusById(
          this.space.campus
        );

        if (response.code === Http.Found && response.data) {
          response.data._key = this.space.campus;
          this.listNameCampus.push(response.data);
          this.campusIsInactive = true;
        }
      }
    } catch (error) {
      const errorMessage =
        (error as Error).message === GENERAL_CONSTANTS.FORBIDDEN
          ? `${FORM_MESSAGES.FORBIDDEN_REQUEST}${CAMPUS_ACCESS.GET}`
          : FORM_MESSAGES.REQUEST_FAILED;

      Snackbar.popError(errorMessage);
    }
  }

  async validateUniqueName() {
    if (!this.campusModel[0].name || this.campusModel[0].name != "") {
      this.uniqueName = false;
      this.errorsName = await Utils.checkUniqueName(
        COLLECTIONS_NAME.SPACE,
        this.spaceName,
        this.campusModel[0].id
      );
      this.uniqueName = this.errorsName.length <= 0 && this.spaceName != "";
    }
  }
}
